import React, { useEffect, useState } from "react";
import { TL } from "../i18n";
import { Navigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import client, { apiErr } from "../lib/api";
import { Button, Card } from "../components/ui";
import { Ticket, Copy, RefreshCw, Check } from "lucide-react";

const APPS = [
  { value: "family_tree", label: "Family Tree" },
  { value: "holdings", label: "Holdings Management" },
  { value: "currency", label: "Currency Management" },
  { value: "benefits", label: "Benefits Management" },
];

const STATUS_STYLES = {
  active: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
  used: "bg-black/10 text-black/60 dark:bg-white/10 dark:text-white/60",
  expired: "bg-[#DA1E28]/15 text-[#DA1E28]",
};

export default function DemoCodes() {
  const { isAdmin, isSupport } = useApp();
  const [kind, setKind] = useState("suite");
  const [app, setApp] = useState("family_tree");
  const [result, setResult] = useState(null);
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState("");

  const load = () => client.get("/admin/demo/passcodes")
    .then((r) => setRows(r.data.passcodes || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!isAdmin && !isSupport) return <Navigate to="/app" replace />;

  const generate = async () => {
    setErr(""); setBusy(true); setResult(null);
    try {
      const body = kind === "single" ? { kind, app } : { kind };
      const { data } = await client.post("/admin/demo/passcode", body);
      setResult(data);
      load();
    } catch (e) { setErr(apiErr(e)); }
    setBusy(false);
  };

  const copy = (txt, what) => { navigator.clipboard?.writeText(txt); setCopied(what); setTimeout(() => setCopied(""), 1200); };

  return (
    <div className="space-y-6" data-testid="demo-codes-page">
      <div className="flex items-center gap-2">
        <Ticket size={22} className="text-[var(--primary)]" />
        <h1 className="font-head text-2xl">{TL("Demo access codes")}</h1>
      </div>
      <p className="max-w-2xl text-sm opacity-70">
        Generate a single-use code bound to one private (non-public) demo instance and share it with a customer.
        They redeem it at <b>{TL("/demo-login")}</b> to open that demo. Codes expire automatically.
      </p>

      <Card data-testid="demo-code-generator">
        <h2 className="mb-3 font-head text-lg">{TL("Generate a code")}</h2>
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm">Demo type
            <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="demo-code-kind"
              className="mt-1 block w-48 rounded-md border border-black/15 bg-white/70 px-3 py-2 text-sm dark:border-white/15 dark:bg-black/30">
              <option value="suite">{TL("Full suite (all 4 apps)")}</option>
              <option value="single">{TL("Single application")}</option>
            </select>
          </label>
          {kind === "single" && (
            <label className="text-sm">Application
              <select value={app} onChange={(e) => setApp(e.target.value)} data-testid="demo-code-app"
                className="mt-1 block w-56 rounded-md border border-black/15 bg-white/70 px-3 py-2 text-sm dark:border-white/15 dark:bg-black/30">
                {APPS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
              </select>
            </label>
          )}
          <Button onClick={generate} disabled={busy} data-testid="demo-code-generate">
            {busy ? "Generating…" : "Generate code"}
          </Button>
        </div>
        {err && <p className="mt-3 text-sm text-[#DA1E28]" data-testid="demo-code-error">{err}</p>}
        {result && (
          <div className="mt-4 rounded-lg border border-[var(--primary)]/30 bg-[var(--primary)]/5 p-4" data-testid="demo-code-result">
            <div className="flex flex-wrap items-center gap-3">
              <button onClick={() => copy(result.code, "code")} title={TL("Copy code")}
                className="font-mono text-2xl font-bold tracking-widest text-[var(--primary)]" data-testid="demo-code-value">
                {result.code} {copied === "code" ? <Check size={16} className="inline" /> : <Copy size={16} className="inline opacity-60" />}
              </button>
            </div>
            <div className="mt-1 text-sm opacity-70">
              Instance <b>{result.instance}</b> · {result.name} · expires in {result.ttl_hours}h
              ({(result.expires_at || "").slice(0, 16).replace("T", " ")} UTC)
            </div>
            <p className="mt-2 text-xs opacity-60">{TL("Single-use. Give the customer this code and the /demo-login link.")}</p>
          </div>
        )}
      </Card>

      <Card data-testid="demo-code-list">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-head text-lg">{TL("Recent codes")}</h2>
          <button onClick={load} className="flex items-center gap-1 text-sm opacity-70 hover:opacity-100" data-testid="demo-code-refresh">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
        {rows.length === 0 && <p className="text-sm opacity-60">{TL("No codes generated yet.")}</p>}
        <div className="space-y-1.5">
          {rows.map((p) => (
            <div key={p.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-black/10 px-3 py-2 text-sm dark:border-white/10"
              data-testid={`demo-code-row-${p.code}`}>
              <span className="font-mono font-semibold tracking-wider">{p.code}</span>
              <span className="opacity-70">{p.demo_username}</span>
              <span className="opacity-50">{p.kind === "single" ? p.app : "suite"}</span>
              <span className={`ms-auto rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[p.status] || ""}`}
                data-testid={`demo-code-status-${p.code}`}>{p.status}</span>
              <span className="text-xs opacity-50">exp {(p.expires_at || "").slice(0, 10)}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
